import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Database, Download, Upload, AlertTriangle, CheckCircle2, Trash2, Plus, PencilLine, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fmtIDR } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import DuplicateMergeCard from "@/components/DuplicateMergeCard";

const TYPES = [
  { v: "patients_simple", l: "Pasien (Sederhana)", desc: "Import cepat data pasien lama — kolom: nama, no HP, No. RM (opsional), alamat. No. RM kosong akan dibuat otomatis. Cocok untuk data minim." },
  { v: "patients", l: "Pasien (Lengkap)", desc: "Database customer existing (nama, NIK, no HP, email, tgl lahir, cabang, deposit, dll.)." },
  { v: "treatments", l: "Treatment", desc: "Daftar layanan/treatment beserta harga & durasi." },
  { v: "products", l: "Produk / Stok", desc: "Stok produk & BHP + HPP. Kolom: name, category (bhp/retail/equipment/medication/other), sku, unit, quantity, min_stock, unit_cost (HPP), branch_name, notes." },
  { v: "doctors", l: "Dokter", desc: "Dokter aktif + email (untuk login & komisi) + cabang + commission rate (default 10%)." },
  { v: "therapists", l: "Therapist", desc: "Therapist + email (untuk login & komisi) + cabang + commission per treatment (default Rp 50.000)." },
  { v: "expenses", l: "Pengeluaran", desc: "Histori biaya operasional (gaji, marketing, sewa, dll.) untuk laporan P&L." },
];

const MANUAL_CATS = [
  { v: "treatments", l: "Treatment", ep: "/treatments", fields: [["name", "Nama Treatment", "text"], ["category", "Kategori", "text"], ["price", "Harga (Rp)", "number"], ["duration_min", "Durasi (menit)", "number"]] },
  { v: "doctors", l: "Dokter", ep: "/doctors", fields: [["name", "Nama Dokter", "text"], ["email", "Email (untuk login & komisi)", "email"], ["specialty", "Spesialisasi", "text"], ["commission_rate_pct", "Komisi Umum (%)", "number"], ["all_alat_percentage", "All Alat % (HIFU, IPL, RF, Laser)", "number"], ["suntik_percentage", "Suntik % (Skinbooster/PRP)", "number"], ["contouring_percentage", "Contouring % (Botox, Filler, TL)", "number"]] },
  { v: "therapists", l: "Therapist", ep: "/therapists", fields: [["name", "Nama Therapist", "text"], ["email", "Email (untuk login & komisi)", "email"], ["commission_per_treatment", "Komisi / treatment (Rp)", "number"], ["facial_fee", "Facial Fee (Rp)", "number"], ["all_alat_fee", "All Alat Fee (Rp) — HIFU, IPL, RF, Laser", "number"], ["skinbooster_fee", "Skinbooster Fee (Rp)", "number"]] },
  { v: "products", l: "Produk / Obat (Retail)", ep: "/inventory", fields: [["name", "Nama Produk/Obat", "text"], ["unit", "Satuan (pcs/botol/box)", "text"], ["quantity", "Stok Awal", "number"], ["min_stock", "Stok Minimum", "number"], ["unit_cost", "HPP (Rp)", "number"], ["sell_price", "Harga Jual (Rp)", "number"]] },
  { v: "bhp", l: "BHP (Bahan Habis Pakai)", ep: "/inventory", presetCat: "bhp", fields: [["name", "Nama BHP", "text"], ["unit", "Satuan (pcs/box)", "text"], ["quantity", "Stok Awal", "number"], ["min_stock", "Stok Minimum", "number"], ["unit_cost", "HPP (Rp)", "number"]] },
  { v: "equipment", l: "Equipment (Alat)", ep: "/inventory", presetCat: "equipment", fields: [["name", "Nama Alat", "text"], ["unit", "Satuan (unit)", "text"], ["quantity", "Jumlah", "number"], ["min_stock", "Stok Minimum", "number"], ["unit_cost", "Nilai / HPP (Rp)", "number"]] },
  { v: "obat", l: "Obat (Medication)", ep: "/inventory", presetCat: "medication", fields: [["name", "Nama Obat", "text"], ["unit", "Satuan (strip/botol)", "text"], ["quantity", "Stok Awal", "number"], ["min_stock", "Stok Minimum", "number"], ["unit_cost", "HPP (Rp)", "number"], ["sell_price", "Harga Jual (Rp)", "number"]] },
  { v: "member", l: "Member", custom: true },
  { v: "treatment_deposit", l: "Deposit Treatment", custom: true },
];
const MEMBER_TIERS = ["Silver", "Gold", "Platinum"];
const PRODUCT_CATEGORIES = ["bhp", "retail", "equipment", "medication", "other"];

export default function Migration() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [tab, setTab] = useState("patients_simple");
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);
  // Manual add
  const [manualCat, setManualCat] = useState("treatments");
  const [manualForm, setManualForm] = useState({});
  const [manualBusy, setManualBusy] = useState(false);
  const [branches, setBranches] = useState([]);
  const [importBranch, setImportBranch] = useState("");
  const [prodCategory, setProdCategory] = useState("retail");
  const [prodBranch, setProdBranch] = useState("");
  const [treatmentsList, setTreatmentsList] = useState([]);
  // Katalog Paket Member
  const [memberPkgs, setMemberPkgs] = useState([]);
  const [pkgForm, setPkgForm] = useState({ name: "", price: "", validity_months: "12" });
  const [pkgEditId, setPkgEditId] = useState(null);
  const [pkgBusy, setPkgBusy] = useState(false);
  const loadMemberPkgs = () => api.get("/member-packages").then((r) => setMemberPkgs(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/branches").then((r) => {
      setBranches(r.data);
      if (r.data[0]) { setProdBranch(r.data[0].id); setImportBranch(r.data[0].id); }
    }).catch(() => {});
    api.get("/treatments").then((r) => setTreatmentsList(r.data)).catch(() => {});
    loadMemberPkgs();
  }, []);

  const savePkg = async () => {
    if (!pkgForm.name.trim()) { toast.error("Nama paket wajib diisi"); return; }
    if (!(Number(pkgForm.price) > 0)) { toast.error("Harga paket harus lebih dari 0"); return; }
    setPkgBusy(true);
    try {
      const body = { name: pkgForm.name.trim(), price: Number(pkgForm.price), validity_months: Number(pkgForm.validity_months || 12), active: true };
      if (pkgEditId) await api.patch(`/member-packages/${pkgEditId}`, body);
      else await api.post("/member-packages", body);
      toast.success(pkgEditId ? "Paket member diperbarui" : "Paket member ditambahkan");
      setPkgForm({ name: "", price: "", validity_months: "12" }); setPkgEditId(null);
      loadMemberPkgs();
    } catch (e) { toast.error(formatApiError(e)); } finally { setPkgBusy(false); }
  };
  const editPkg = (p) => { setPkgEditId(p.id); setPkgForm({ name: p.name, price: String(p.price), validity_months: String(p.validity_months) }); };
  const delPkg = async (id) => {
    if (!window.confirm("Hapus paket member ini?")) return;
    try { await api.delete(`/member-packages/${id}`); loadMemberPkgs(); toast.success("Paket dihapus"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const submitMember = async () => {
    if (!manualForm.name) { toast.error("Nama pasien wajib diisi"); return; }
    if (!manualForm.member_tier) { toast.error("Kategori Member wajib dipilih"); return; }
    setManualBusy(true);
    try {
      const { data } = await api.post("/migration/manual/member", {
        name: manualForm.name, phone: manualForm.phone || "",
        member_tier: manualForm.member_tier, nominal: Number(manualForm.nominal || 0),
        expired_date: manualForm.expired_date || null,
      });
      toast.success(`Member ${data.member_tier} di-set untuk ${data.name}${data.created_patient ? " (pasien baru dibuat)" : ""}`);
      setManualForm({});
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Gagal menyimpan member");
    } finally { setManualBusy(false); }
  };

  const submitTreatmentDeposit = async () => {
    if (!manualForm.name) { toast.error("Nama pasien wajib diisi"); return; }
    if (!manualForm.treatment_id) { toast.error("Treatment wajib dipilih"); return; }
    if (!(Number(manualForm.price) > 0)) { toast.error("Harga treatment harus > 0"); return; }
    setManualBusy(true);
    try {
      const { data } = await api.post("/migration/manual/treatment-deposit", {
        name: manualForm.name, phone: manualForm.phone || "",
        deposit_date: manualForm.deposit_date || null, treatment_id: manualForm.treatment_id,
        price: Number(manualForm.price), redeem_note: manualForm.redeem_note || "",
      });
      toast.success(`Deposit ${data.treatment_name} tersimpan untuk ${data.patient_name} (saldo Rp ${Number(data.nominal_remaining).toLocaleString("id-ID")})`);
      setManualForm({});
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Gagal menyimpan deposit");
    } finally { setManualBusy(false); }
  };

  const submitManual = async () => {
    const cat = MANUAL_CATS.find((c) => c.v === manualCat);
    if (!manualForm.name) { toast.error("Nama wajib diisi"); return; }
    setManualBusy(true);
    try {
      let payload = { ...manualForm };
      ["price", "duration_min", "commission_rate_pct", "commission_per_treatment", "quantity", "min_stock", "unit_cost", "sell_price", "all_alat_percentage", "suntik_percentage", "contouring_percentage", "facial_fee", "all_alat_fee", "skinbooster_fee"].forEach((k) => {
        if (payload[k] !== undefined && payload[k] !== "") payload[k] = Number(payload[k]);
      });
      if (cat.ep === "/inventory") {
        if (!prodBranch) { toast.error("Pilih cabang untuk item ini"); setManualBusy(false); return; }
        payload.category = cat.presetCat || prodCategory;
        payload.branch_id = prodBranch;
      }
      const { data } = await api.post(cat.ep, payload);
      toast.success(`${cat.l} "${data.name}" berhasil ditambahkan`);
      setManualForm({});
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Gagal menambah data");
    } finally {
      setManualBusy(false);
    }
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem("myclinic_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/migration/template/${tab}`;
    try {
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("download gagal");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `template_${tab}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Template terunduh");
    } catch {
      toast.error("Gagal unduh template");
    }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const token = localStorage.getItem("myclinic_token");
      const activeClinic = localStorage.getItem("myclinic_active_clinic");
      const headers = { Authorization: `Bearer ${token}` };
      if (activeClinic) headers["X-Clinic-Id"] = activeClinic;
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/migration/import/${tab}${importBranch ? `?branch_id=${encodeURIComponent(importBranch)}` : ""}`;
      const r = await fetch(url, {
        method: "POST",
        headers,
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Import gagal");
      setResult(data);
      toast.success(`Berhasil import ${data.imported} baris`);
    } catch (err) {
      toast.error(err.message || "Import gagal");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const resetTotal = async () => {
    if (resetConfirm !== "KONFIRMASI RESET") { toast.error("Ketik persis: KONFIRMASI RESET"); return; }
    setResetting(true);
    const token = localStorage.getItem("myclinic_token");
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reset-total?confirm=${encodeURIComponent("KONFIRMASI RESET")}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Reset gagal");
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Reset Total selesai. ${total} dokumen dihapus. Hanya akun Super Admin dipertahankan.`);
      setResetOpen(false);
      setResetConfirm("");
      localStorage.removeItem("myclinic_active_clinic");
    } catch (err) {
      toast.error(err.message || "Reset gagal");
    } finally {
      setResetting(false);
    }
  };

  const tabInfo = TYPES.find((t) => t.v === tab);

  return (
    <div className="space-y-6" data-testid="migration-page">
      <div>
        <h1 className="font-heading text-3xl tracking-tight">Data Migration</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Migrasi data dari sistem lama. Download template, lengkapi data, upload file CSV atau Excel (.xlsx).
          Data otomatis masuk sistem & langsung tersinkron ke POS/Booking semua user pada clinic aktif.
        </p>
      </div>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setResult(null); }}>
        <TabsList data-testid="migration-tabs">
          {TYPES.map((t) => (
            <TabsTrigger key={t.v} value={t.v} data-testid={`tab-${t.v}`}>{t.l}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Database className="h-4 w-4" /> Import {tabInfo?.l}
          </CardTitle>
          <CardDescription>{tabInfo?.desc}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm">Cabang Tujuan Import</Label>
            <Select value={importBranch} onValueChange={setImportBranch}>
              <SelectTrigger data-testid="import-branch-select" className="mt-1 w-full sm:w-80"><SelectValue placeholder="Pilih cabang tujuan" /></SelectTrigger>
              <SelectContent>
                {branches.map((b) => (
                  <SelectItem key={b.id} value={b.id} data-testid={`import-branch-${b.id}`}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              Semua baris di file akan masuk ke cabang ini. <b>Pastikan pilih cabang yang benar</b> sebelum upload (mis. file Sidoarjo → pilih cabang Sidoarjo).
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" data-testid="download-template-btn" onClick={downloadTemplate}>
              <Download className="mr-1.5 h-4 w-4" /> Download Template CSV
            </Button>
            <Button data-testid="upload-csv-btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
              <Upload className="mr-1.5 h-4 w-4" /> {uploading ? "Memproses..." : "Upload CSV / Excel"}
            </Button>
            <input ref={fileRef} type="file" accept=".csv,text/csv,.xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={upload} data-testid="csv-file-input" />
          </div>

          <div className="rounded-md bg-amber-50/60 border border-amber-200 p-3 text-xs text-amber-800">
            <div className="font-medium flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Tips Import</div>
            <ul className="mt-1.5 ml-4 list-disc space-y-0.5">
              <li>Header fleksibel: <code>Nama</code>, <code>No HP</code>, <code>No RM</code> otomatis dikenali. Encoding UTF-8.</li>
              <li><b>Pilih Cabang Tujuan</b> di atas agar data masuk ke cabang yang benar (tidak tertukar).</li>
              <li>Pasien di-dedupe berdasarkan <b>No. RM</b> (unik), jadi pasien yang berbagi no HP (mis. keluarga) tetap masuk semua.</li>
              <li>No. RM kosong akan dibuat otomatis. Field kosong = optional.</li>
            </ul>
          </div>

          {result && (
            <div className="rounded-md border border-border p-4 space-y-2" data-testid="import-result">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <span className="font-medium text-sm">Hasil Import</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div className="rounded bg-emerald-50 p-2"><div className="text-muted-foreground">Imported</div><div className="font-heading text-xl text-emerald-700">{result.imported}</div></div>
                <div className="rounded bg-sky-50 p-2"><div className="text-muted-foreground">Total Baris</div><div className="font-heading text-xl">{result.total_rows}</div></div>
                <div className="rounded bg-amber-50 p-2"><div className="text-muted-foreground">Skip Duplikat</div><div className="font-heading text-xl text-amber-700">{result.skipped_duplicate}</div></div>
                <div className="rounded bg-rose-50 p-2"><div className="text-muted-foreground">Error</div><div className="font-heading text-xl text-rose-700">{result.error_count}</div></div>
              </div>
              {result.skipped_details?.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground">Lihat detail skip ({result.skipped_duplicate})</summary>
                  <ul className="mt-2 space-y-0.5 max-h-48 overflow-y-auto" data-testid="import-skipped-list">
                    {result.skipped_details.map((e, i) => (
                      <li key={i} className="rounded bg-amber-50 px-2 py-1">Row {e.row}: {e.reason}</li>
                    ))}
                  </ul>
                </details>
              )}
              {result.errors?.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground">Lihat detail error (max 50)</summary>
                  <ul className="mt-2 space-y-0.5 max-h-48 overflow-y-auto">
                    {result.errors.map((e, i) => (
                      <li key={i} className="rounded bg-rose-50 px-2 py-1">Row {e.row}: {e.error}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual add — fallback jika migrasi gagal */}
      <Card data-testid="manual-add-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <PencilLine className="h-4 w-4" /> Tambah Data Manual
          </CardTitle>
          <CardDescription>
            Gunakan ini jika import file gagal/tidak terbaca. Tambah satu per satu: Treatment, Dokter, Therapist, atau Produk/Obat.
            Data langsung masuk & tersinkron ke POS clinic aktif.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Kategori</Label>
            <Select value={manualCat} onValueChange={(v) => { setManualCat(v); setManualForm({}); }}>
              <SelectTrigger data-testid="manual-cat-select" className="mt-1 w-full sm:w-64"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MANUAL_CATS.map((c) => (<SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {manualCat === "member" && (
              <>
                <div>
                  <Label>Nama Pasien</Label>
                  <Input data-testid="manual-field-name" className="mt-1" value={manualForm.name ?? ""} onChange={(e) => setManualForm({ ...manualForm, name: e.target.value })} />
                </div>
                <div>
                  <Label>No WhatsApp</Label>
                  <Input data-testid="manual-field-phone" className="mt-1" value={manualForm.phone ?? ""} onChange={(e) => setManualForm({ ...manualForm, phone: e.target.value })} />
                </div>
                <div>
                  <Label>Kategori Member</Label>
                  <Select value={manualForm.member_tier ?? ""} onValueChange={(v) => setManualForm({ ...manualForm, member_tier: v })}>
                    <SelectTrigger data-testid="manual-field-member_tier" className="mt-1"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                    <SelectContent>{MEMBER_TIERS.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Nominal Transaksi (Rp)</Label>
                  <Input type="number" data-testid="manual-field-nominal" className="mt-1" value={manualForm.nominal ?? ""} onChange={(e) => setManualForm({ ...manualForm, nominal: e.target.value })} />
                </div>
                <div>
                  <Label>Expired Date</Label>
                  <Input type="date" data-testid="manual-field-expired_date" className="mt-1" value={manualForm.expired_date ?? ""} onChange={(e) => setManualForm({ ...manualForm, expired_date: e.target.value })} />
                </div>
              </>
            )}
            {manualCat === "treatment_deposit" && (
              <>
                <div>
                  <Label>Nama Pasien</Label>
                  <Input data-testid="manual-field-name" className="mt-1" value={manualForm.name ?? ""} onChange={(e) => setManualForm({ ...manualForm, name: e.target.value })} />
                </div>
                <div>
                  <Label>No WhatsApp</Label>
                  <Input data-testid="manual-field-phone" className="mt-1" value={manualForm.phone ?? ""} onChange={(e) => setManualForm({ ...manualForm, phone: e.target.value })} />
                </div>
                <div>
                  <Label>Tanggal Deposit</Label>
                  <Input type="date" data-testid="manual-field-deposit_date" className="mt-1" value={manualForm.deposit_date ?? ""} onChange={(e) => setManualForm({ ...manualForm, deposit_date: e.target.value })} />
                </div>
                <div>
                  <Label>Treatment</Label>
                  <Select value={manualForm.treatment_id ?? ""} onValueChange={(v) => setManualForm({ ...manualForm, treatment_id: v })}>
                    <SelectTrigger data-testid="manual-field-treatment_id" className="mt-1"><SelectValue placeholder="Pilih treatment" /></SelectTrigger>
                    <SelectContent>{treatmentsList.map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Harga Treatment (Rp)</Label>
                  <Input type="number" data-testid="manual-field-price" className="mt-1" value={manualForm.price ?? ""} onChange={(e) => setManualForm({ ...manualForm, price: e.target.value })} />
                </div>
                <div className="sm:col-span-2">
                  <Label>Keterangan Redeem</Label>
                  <Input data-testid="manual-field-redeem_note" className="mt-1" value={manualForm.redeem_note ?? ""} onChange={(e) => setManualForm({ ...manualForm, redeem_note: e.target.value })} placeholder="Catatan penggunaan deposit" />
                </div>
              </>
            )}
            {!MANUAL_CATS.find((c) => c.v === manualCat).custom && MANUAL_CATS.find((c) => c.v === manualCat).fields.map(([key, label, type]) => (
              <div key={key}>
                <Label>{label}</Label>
                <Input
                  data-testid={`manual-field-${key}`}
                  type={type}
                  className="mt-1"
                  value={manualForm[key] ?? ""}
                  onChange={(e) => setManualForm({ ...manualForm, [key]: e.target.value })}
                />
              </div>
            ))}
            {!MANUAL_CATS.find((c) => c.v === manualCat).custom && MANUAL_CATS.find((c) => c.v === manualCat).ep === "/inventory" && (
              <>
                {manualCat === "products" && (
                  <div>
                    <Label>Kategori Produk</Label>
                    <Select value={prodCategory} onValueChange={setProdCategory}>
                      <SelectTrigger data-testid="manual-prod-category" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PRODUCT_CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Cabang</Label>
                  <Select value={prodBranch} onValueChange={setProdBranch}>
                    <SelectTrigger data-testid="manual-prod-branch" className="mt-1"><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
                    <SelectContent>
                      {branches.map((b) => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>

          <Button data-testid="manual-add-submit" onClick={manualCat === "member" ? submitMember : (manualCat === "treatment_deposit" ? submitTreatmentDeposit : submitManual)} disabled={manualBusy}>
            <Plus className="mr-1.5 h-4 w-4" /> {manualBusy ? "Menyimpan..." : "Tambah"}
          </Button>
        </CardContent>
      </Card>

      {/* Katalog Paket Member — dikelola klinik, muncul di POS */}
      <Card data-testid="member-package-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <PencilLine className="h-4 w-4" /> Katalog Paket Member
          </CardTitle>
          <CardDescription>
            Kelola daftar Paket Member (nama, harga, masa berlaku). Paket aktif akan muncul sebagai pilihan di menu POS.
            Saat dijual, status member pasien otomatis aktif + diberi tanggal kedaluwarsa.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {memberPkgs.length > 0 && (
            <div className="rounded-md border border-border divide-y" data-testid="member-package-list">
              {memberPkgs.map((p) => (
                <div key={p.id} className="flex items-center gap-3 px-3 py-2" data-testid={`member-pkg-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{p.name}{!p.active && <span className="ml-2 text-[10px] text-muted-foreground">(nonaktif)</span>}</div>
                    <div className="text-xs text-muted-foreground">{fmtIDR(p.price)} · berlaku {p.validity_months} bln</div>
                  </div>
                  <Button type="button" size="sm" variant="ghost" data-testid={`member-pkg-edit-${p.id}`} onClick={() => editPkg(p)}><PencilLine className="h-4 w-4" /></Button>
                  <Button type="button" size="sm" variant="ghost" className="text-rose-600" data-testid={`member-pkg-del-${p.id}`} onClick={() => delPkg(p.id)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label>Nama Paket</Label>
              <Input data-testid="member-pkg-name" className="mt-1" value={pkgForm.name} onChange={(e) => setPkgForm({ ...pkgForm, name: e.target.value })} placeholder="mis. Gold Member" />
            </div>
            <div>
              <Label>Harga (Rp)</Label>
              <Input type="number" data-testid="member-pkg-price" className="mt-1" value={pkgForm.price} onChange={(e) => setPkgForm({ ...pkgForm, price: e.target.value })} placeholder="mis. 2500000" />
            </div>
            <div>
              <Label>Masa Berlaku (bulan)</Label>
              <Input type="number" data-testid="member-pkg-validity" className="mt-1" value={pkgForm.validity_months} onChange={(e) => setPkgForm({ ...pkgForm, validity_months: e.target.value })} placeholder="12" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button data-testid="member-pkg-save" onClick={savePkg} disabled={pkgBusy}>
              <Plus className="mr-1.5 h-4 w-4" /> {pkgBusy ? "Menyimpan..." : (pkgEditId ? "Simpan Perubahan" : "Tambah Paket")}
            </Button>
            {pkgEditId && (
              <Button variant="outline" data-testid="member-pkg-cancel" onClick={() => { setPkgEditId(null); setPkgForm({ name: "", price: "", validity_months: "12" }); }}>Batal</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {["owner", "super_admin"].includes(user?.role) && <DuplicateMergeCard />}

      {isSuper && (
        <Card className="border-rose-300">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2 text-rose-800">
              <AlertTriangle className="h-4 w-4" /> Reset Total (Hapus Semua Data)
            </CardTitle>
            <CardDescription>
              Menghapus SELURUH data di semua clinic — clinics, cabang, users, pasien, transaksi,
              stok, rekam medis, dll. Hanya akun <b>Super Admin</b> Anda yang dipertahankan. Aksi ini <b>tidak bisa di-undo</b>.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Badge variant="outline" className="text-xs border-rose-300 text-rose-700">Super Admin only</Badge>
            <Dialog open={resetOpen} onOpenChange={(o) => { setResetOpen(o); if (!o) setResetConfirm(""); }}>
              <DialogTrigger asChild>
                <Button data-testid="reset-total-btn" className="bg-rose-600 hover:bg-rose-700 text-white">
                  <Trash2 className="h-4 w-4 mr-2" /> Reset Total (Hapus Semua Data)
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="reset-total-dialog">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-rose-700">
                    <AlertTriangle className="h-5 w-5" /> Konfirmasi Reset Total
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 py-2">
                  <div className="rounded-md bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800">
                    Semua data di SEMUA clinic akan dihapus permanen. Hanya akun Super Admin yang tersisa.
                    Data demo/dummy TIDAK akan muncul lagi setelah reset.
                  </div>
                  <div>
                    <Label>Ketik <span className="font-mono font-semibold">KONFIRMASI RESET</span> untuk melanjutkan:</Label>
                    <Input
                      data-testid="reset-total-confirm-input"
                      className="mt-1.5"
                      value={resetConfirm}
                      onChange={(e) => setResetConfirm(e.target.value)}
                      placeholder="KONFIRMASI RESET"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setResetOpen(false)}>Batal</Button>
                  <Button
                    data-testid="reset-total-confirm-btn"
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    onClick={resetTotal}
                    disabled={resetting || resetConfirm !== "KONFIRMASI RESET"}
                  >
                    {resetting ? "Menghapus..." : "Hapus Semua Data Sekarang"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
